import React, { memo } from "react";

import { Grid } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import { ModalTableWrapper } from "components/AgGrid/TableWrapper";
import { Button, SecondaryButton } from "components/Form/Button";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { ApiModel } from "constants/loading";

import { getBusinessById, getContextByModalType } from "selectors";

import { openDispatchingModal } from "actions";
import { ModalTypes } from "constants/navigation";
import { BusinessPaymentsTable } from "./BusinessPaymentsTable";

interface BusinessPaymentsModalTitleProps {
  businessId: string;
  onClose: () => void;
}

function BusinessPaymentsModalTitleComponent(
  props: BusinessPaymentsModalTitleProps,
) {
  const { businessId, onClose } = props;

  const businessName = useSelector<State, Business | null>(
    getBusinessById(businessId),
  )?.name;

  return (
    <DialogTitle onClose={onClose}>Payments from {businessName}</DialogTitle>
  );
}

const BusinessPaymentsModalTitle = memo(BusinessPaymentsModalTitleComponent);

interface ModalContext {
  businessId: string;
}

function useModalContext(): ModalContext | null {
  return useSelector(getContextByModalType(ModalTypes.BusinessPayments));
}

interface BusinessPaymentsModalProps {
  onClose: () => void;
}

export function BusinessPaymentsModal(
  props: BusinessPaymentsModalProps,
): React.JSX.Element {
  const { onClose } = props;

  const modalContext: ModalContext = useModalContext();

  const businessId = modalContext?.businessId ?? null;

  const dispatch = useDispatch();

  function onClickRecordPayment() {
    dispatch(
      openDispatchingModal(
        ModalTypes.EditPayment,
        window.location.hash,
        {
          values: {},
        },
        { fromBusinessId: businessId },
      ),
    );
  }

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="md" fullWidth>
      <WaitForSync requiredData={[ApiModel.BUSINESSES]}>
        <BusinessPaymentsModalTitle onClose={onClose} businessId={businessId} />
        <DialogContent dividers>
          <Grid spacing={2} container>
            <Grid item>
              <Button data-tour="recordPayment" onClick={onClickRecordPayment}>
                Record Payment
              </Button>
            </Grid>
          </Grid>
          <ModalTableWrapper>
            <WaitForSync requiredData={[ApiModel.PAYMENTS]}>
              <BusinessPaymentsTable businessId={businessId} />
            </WaitForSync>
          </ModalTableWrapper>
        </DialogContent>
        <DialogActions>
          <SecondaryButton data-tour="close" type="button" onClick={onClose}>
            Close
          </SecondaryButton>
        </DialogActions>
      </WaitForSync>
    </ZoomyDialog>
  );
}
